import { useMemo, useState } from "react";
import { DownOutlined, InfoCircleOutlined, RightOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import ToggleSwitch from "../../../../shared/ToggleSwitch/ToggleSwitch";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import {
  ACTION_CATEGORY_GENERAL,
  groupActionsByCategory,
} from "../../../../../utils/constants/permissionsConstants";
import { SIDEBAR_ICONS } from "../../constants";
import GeneralIcon from "../../../../../assets/icons/ic_configuration_new.svg";
import styles from "./ActionsByCategory.module.css";

/** `general` is not a sidebar tab, so it needs an icon of its own. */
const CATEGORY_ICONS: Record<string, string> = {
  ...SIDEBAR_ICONS,
  [ACTION_CATEGORY_GENERAL]: GeneralIcon,
};

export type ActionsByCategoryAction = {
  key: string;
  display_name?: string;
  /** Tooltip text beside the label, for a row whose effect isn't obvious. */
  info?: string;
  /** Locks this row alone. OR'd with the column-wide `disabled`. */
  disabled?: boolean;
};

/** Payload shape the New Staff / Roles action handlers already expect. */
export type ActionTogglePayload = {
  event_code?: unknown;
  is_active: boolean;
  eventCode: string;
};

export type ActionsByCategoryProps = {
  actions: ActionsByCategoryAction[];
  /** Granted actions, keyed by action key. */
  values?: Record<string, boolean>;
  onActionToggle?: (payload: ActionTogglePayload) => void;
  /** Locks every row. Individual rows can also lock themselves. */
  disabled?: boolean;
};

/**
 * `available_actions` grouped into the top-level sidebar categories, one
 * collapsible section each, so the column reads with the same taxonomy as the
 * Sidebar Preview beside it.
 *
 * A collapsed section still reports its granted/total count, which is what
 * makes the list scannable: a reviewer sees "Users 3/13" without opening
 * anything. Sections holding a granted action open by default — the rest stay
 * shut, since an all-off section has nothing to show that the count doesn't
 * already say.
 */
function ActionsByCategory({
  actions,
  values = {},
  onActionToggle,
  disabled = false,
}: ActionsByCategoryProps) {
  const groups = useMemo(
    () => groupActionsByCategory(actions, AppStrings.PAM_GENERAL),
    [actions],
  );

  const defaultExpanded = useMemo(() => {
    const acc: Record<string, boolean> = {};
    groups.forEach((group) => {
      acc[group.id] = group.actions.some((action) =>
        Boolean(values[action.key]),
      );
    });
    return acc;
  }, [groups, values]);

  // Explicit clicks win over the derived default, so selecting a different role
  // never snaps a section the user opened back shut.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  const expanded = useMemo(
    () => ({ ...defaultExpanded, ...overrides }),
    [defaultExpanded, overrides],
  );

  // Flipping the last granted switch off removes the reason a section
  // auto-expanded, which would collapse it out from under the cursor. Pin it
  // open the moment the user touches anything inside it.
  const pinOpen = (categoryId: string) => {
    setOverrides((prev) => ({ ...prev, [categoryId]: true }));
  };

  const toggleCategory = (categoryId: string) => {
    setOverrides((prev) => {
      const isOpen = Object.prototype.hasOwnProperty.call(prev, categoryId)
        ? prev[categoryId]
        : Boolean(defaultExpanded[categoryId]);
      return { ...prev, [categoryId]: !isOpen };
    });
  };

  if (!groups.length) return null;

  return (
    <div className={styles.container}>
      {groups.map((group) => {
        const isOpen = Boolean(expanded[group.id]);
        const grantedCount = group.actions.filter((action) =>
          Boolean(values[action.key]),
        ).length;
        // Categories without an icon of their own fall back to a dot.
        const iconSrc = CATEGORY_ICONS[group.id] ?? null;

        return (
          <div key={group.id} className={styles.group}>
            <div
              className={styles.groupHeader}
              role="button"
              tabIndex={0}
              aria-expanded={isOpen}
              data-testid={`action-category-${group.id}`}
              onClick={() => toggleCategory(group.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleCategory(group.id);
                }
              }}
            >
              <span className={styles.groupIcon}>
                {iconSrc ? (
                  <img src={iconSrc} alt="" className={styles.groupIconSvg} />
                ) : (
                  <span className={styles.dot} />
                )}
              </span>
              <span className={styles.groupLabel}>{group.label}</span>
              <span
                className={`${styles.count} ${grantedCount > 0 ? styles.countActive : ""}`}
                data-testid={`action-category-count-${group.id}`}
              >
                {grantedCount}/{group.actions.length}
              </span>
              <span className={styles.caret}>
                {isOpen ? <DownOutlined /> : <RightOutlined />}
              </span>
            </div>

            {isOpen ? (
              <div className={styles.groupBody}>
                {group.actions.map((action) => {
                  const label = action.display_name ?? action.key;
                  return (
                    <div key={action.key} className={styles.item}>
                      <span className={styles.itemLabel}>
                        {label}
                        {action.info ? (
                          <Tooltip
                            title={action.info}
                            placement="top"
                            color="var(--COLOR-PRIMARY_BACKGROUND_COLOR)"
                          >
                            <InfoCircleOutlined
                              className={styles.infoIcon}
                              data-testid={`action-info-${action.key}`}
                              aria-label={action.info}
                            />
                          </Tooltip>
                        ) : null}
                      </span>
                      <ToggleSwitch
                        status={Boolean(values[action.key])}
                        eventCode={action.key}
                        ariaLabel={label}
                        disabled={disabled || Boolean(action.disabled)}
                        updateEventCode={(data) => {
                          pinOpen(group.id);
                          onActionToggle?.({ ...data, eventCode: action.key });
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default ActionsByCategory;
